/**
 * SEARCH-E3／E4：Discovery suggestions typeahead hook。
 * - debounce 250ms
 * - compositionstart 期間不發請求；compositionend 後才允許
 * - AbortController 取消過期請求，避免舊回應覆蓋新 q
 * - draftQuery 僅驅動 suggestion；discovery appliedQuery 由面板擁有
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { journeyDiscoveryApi } from '../../api/journeyDiscoveryApi';

export const SUGGESTION_DEBOUNCE_MS = 250;
export const SUGGESTION_MIN_CODE_POINTS = 2;
export const SUGGESTION_DEFAULT_LIMIT = 8;

function codePointLength(value) {
  return Array.from(String(value ?? '')).length;
}

export function normalizeSuggestionItems(payload) {
  const items = payload?.data?.items ?? payload?.items ?? [];
  if (!Array.isArray(items)) return [];
  return items
    .filter((item) => item && (item.type === 'GEO' || item.type === 'JOURNEY') && item.id != null && item.label)
    .map((item) => ({
      type: item.type,
      id: String(item.id),
      label: String(item.label),
      secondaryLabel: item.secondaryLabel == null ? null : String(item.secondaryLabel),
    }));
}

export default function useDiscoverySuggestions({
  enabled = true,
  limit = SUGGESTION_DEFAULT_LIMIT,
  initialQuery = '',
} = {}) {
  const [query, setQuery] = useState(() => String(initialQuery ?? ''));
  const [items, setItems] = useState([]);
  const [phase, setPhase] = useState('idle'); // idle | loading | ready | error
  const [error, setError] = useState('');
  const [composing, setComposing] = useState(false);

  const composingRef = useRef(false);
  const queryRef = useRef(String(initialQuery ?? ''));
  const abortRef = useRef(null);
  const timerRef = useRef(null);
  const generationRef = useRef(0);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const clearTimer = () => {
    if (timerRef.current != null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const abortInFlight = () => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  };

  const resetSuggestionResults = useCallback(() => {
    clearTimer();
    abortInFlight();
    generationRef.current += 1;
    setItems([]);
    setPhase('idle');
    setError('');
  }, []);

  const fetchSuggestions = useCallback(async (rawQ) => {
    const q = String(rawQ ?? '').trim();
    if (codePointLength(q) < SUGGESTION_MIN_CODE_POINTS) {
      setItems([]);
      setPhase('idle');
      setError('');
      return;
    }
    if (!enabledRef.current) return;
    const generation = ++generationRef.current;
    abortInFlight();
    const controller = new AbortController();
    abortRef.current = controller;
    setPhase('loading');
    setError('');
    try {
      const payload = await journeyDiscoveryApi.suggestions(
        { q, limit },
        { signal: controller.signal },
      );
      if (generation !== generationRef.current) return;
      setItems(normalizeSuggestionItems(payload));
      setPhase('ready');
    } catch (cause) {
      if (cause?.name === 'CanceledError' || cause?.name === 'AbortError' || cause?.code === 'ERR_CANCELED') {
        return;
      }
      if (generation !== generationRef.current) return;
      setItems([]);
      setError(cause?.message || 'suggestions failed');
      setPhase('error');
    }
  }, [limit]);

  const scheduleFetch = useCallback((rawQ) => {
    clearTimer();
    if (composingRef.current || !enabledRef.current) return;
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      if (composingRef.current || !enabledRef.current) return;
      fetchSuggestions(rawQ);
    }, SUGGESTION_DEBOUNCE_MS);
  }, [fetchSuggestions]);

  const onQueryChange = useCallback((value) => {
    const next = value == null ? '' : String(value);
    queryRef.current = next;
    setQuery(next);
    if (!enabledRef.current) {
      resetSuggestionResults();
      return;
    }
    if (codePointLength(next.trim()) < SUGGESTION_MIN_CODE_POINTS) {
      resetSuggestionResults();
      return;
    }
    scheduleFetch(next);
  }, [resetSuggestionResults, scheduleFetch]);

  /** URL／applied 還原 draft；預設不立刻打 suggestion（等 focus／輸入）。 */
  const syncDraftQuery = useCallback((value, { fetch = false } = {}) => {
    const next = value == null ? '' : String(value);
    queryRef.current = next;
    setQuery(next);
    if (!fetch || codePointLength(next.trim()) < SUGGESTION_MIN_CODE_POINTS) {
      resetSuggestionResults();
      return;
    }
    scheduleFetch(next);
  }, [resetSuggestionResults, scheduleFetch]);

  const clear = useCallback(() => {
    queryRef.current = '';
    setQuery('');
    composingRef.current = false;
    setComposing(false);
    resetSuggestionResults();
  }, [resetSuggestionResults]);

  const onCompositionStart = useCallback(() => {
    composingRef.current = true;
    setComposing(true);
    clearTimer();
  }, []);

  const onCompositionEnd = useCallback((value) => {
    composingRef.current = false;
    setComposing(false);
    const next = value == null ? queryRef.current : String(value);
    queryRef.current = next;
    setQuery(next);
    if (!enabledRef.current) return;
    if (codePointLength(next.trim()) < SUGGESTION_MIN_CODE_POINTS) {
      resetSuggestionResults();
      return;
    }
    scheduleFetch(next);
  }, [resetSuggestionResults, scheduleFetch]);

  useEffect(() => () => {
    clearTimer();
    abortInFlight();
  }, []);

  return {
    query,
    items,
    phase,
    error,
    composing,
    onQueryChange,
    onCompositionStart,
    onCompositionEnd,
    syncDraftQuery,
    clear,
    resetSuggestionResults,
  };
}
